import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import './LatestRoles.css';

interface Sector {
  id: string;
  titleNl: string;
  titleEn: string;
  descriptionNl: string;
  descriptionEn: string;
}

const sectorData: Sector[] = [
  {
    id: 'accounting',
    titleNl: 'Accounting',
    titleEn: 'Accounting',
    descriptionNl: 'Wij verbinden bedrijven met top accounting professionals. Van junior accountants tot senior partners, wij vinden de perfecte match voor uw organisatie.',
    descriptionEn: 'We connect businesses with top accounting professionals. From junior accountants to senior partners, we find the perfect match for your organization.',
  },
  {
    id: 'audit',
    titleNl: 'Audit',
    titleEn: 'Audit',
    descriptionNl: 'Wij vinden bekwame auditors en controlerend accountants die zorgen voor nauwkeurigheid, compliance en financiële integriteit van uw organisatie.',
    descriptionEn: 'We find skilled auditors and chartered accountants who ensure accuracy, compliance and financial integrity of your organization.',
  },
  {
    id: 'tax',
    titleNl: 'Tax',
    titleEn: 'Tax',
    descriptionNl: 'Gespecialiseerde werving voor belastingadviseurs en tax managers die zorgen voor compliance, optimale belastingstrategieën en fiscale planning.',
    descriptionEn: 'Specialized recruitment for tax advisors and tax managers ensuring compliance, optimal tax strategies and fiscal planning.',
  },
  {
    id: 'legal',
    titleNl: 'Legal',
    titleEn: 'Legal',
    descriptionNl: 'Gespecialiseerde werving voor juridische professionals binnen de financiële sector, inclusief advocaten, juristen en legal counsels.',
    descriptionEn: 'Specialized recruitment for legal professionals within the financial sector, including lawyers, legal advisors and legal counsels.',
  },
];

const LatestRoles: React.FC = () => {
  const { language } = useLanguage();
  const [selectedSectorId, setSelectedSectorId] = useState('accounting');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const selectedSector = sectorData.find(s => s.id === selectedSectorId);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    // Copy ref value to a variable to use in cleanup
    const currentSection = sectionRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  const getTitle = (sector: Sector) => language === 'nl' ? sector.titleNl : sector.titleEn;
  const getDescription = (sector: Sector) => language === 'nl' ? sector.descriptionNl : sector.descriptionEn;

  const handleSectorChange = (sectorId: string) => {
    if (sectorId !== selectedSectorId) {
      setIsAnimating(true);
      setTimeout(() => {
        setSelectedSectorId(sectorId);
        setIsAnimating(false);
      }, 300);
    }
  };

  return (
    <section ref={sectionRef} className={`latest-roles ${isVisible ? 'visible' : ''}`}>
      <div className="roles-layout">
        <aside className={`roles-sidebar ${isVisible ? 'slide-in-left' : ''}`}>
          <h2 className="sidebar-title">
            {language === 'nl' ? 'Onze Expertises' : 'Our Sectors'}
          </h2>
          <nav className="sector-nav">
            {sectorData.map((sector, index) => (
              <button
                key={sector.id}
                className={`sector-nav-item ${selectedSectorId === sector.id ? 'active' : ''} ${isVisible ? 'slide-in-item' : ''}`}
                onClick={() => handleSectorChange(sector.id)}
                style={{
                  animationDelay: isVisible ? `${index * 0.1}s` : '0s'
                }}
              >
                {getTitle(sector)}
              </button>
            ))}
          </nav>
        </aside>

        <div className={`roles-content ${isVisible ? 'slide-in-right' : ''}`}>
          {selectedSector && (
            <div className={`featured-sector ${isAnimating ? 'fade-out' : 'fade-in'}`}>
              <div className="sector-content-card">
                <h2 className="sector-content-title">{getTitle(selectedSector)}</h2>
                <p className="sector-content-description">{getDescription(selectedSector)}</p>
                <a href={`/jobs?sector=${selectedSector.id}`} className="sector-cta-link">
                  {language === 'nl'
                    ? `Bekijk ${getTitle(selectedSector)} vacatures →`
                    : `View ${getTitle(selectedSector)} roles →`
                  }
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default LatestRoles;
